<template>
  <v-dialog
    v-model="internalValue"
    max-width="560"
    transition="dialog-bottom-transition"
    scrollable
  >
    <v-card v-if="event" class="ds-dialog">
      <!-- Cabeçalho colorido com nome do evento + badges -->
      <v-card-title class="ds-dialog-header">
        <v-icon color="white" class="mr-2 header-icon" size="20">
          {{ isExternal ? "mdi-google" : "mdi-calendar-text" }}
        </v-icon>
        <div class="header-content" data-tour="det-titulo">
          <span class="ds-dialog-title">{{ displayName }}</span>
          <div class="header-badges">
            <v-chip
              v-if="statusLabel"
              x-small
              dark
              class="ev-chip ev-chip--status"
              :style="{ background: statusColor }"
            >
              <v-icon size="11" left>mdi-circle-medium</v-icon>
              {{ statusLabel }}
            </v-chip>
            <v-chip
              v-if="meetingType"
              x-small
              dark
              class="ev-chip"
              :class="
                meetingType === 'online'
                  ? 'ev-chip--online'
                  : 'ev-chip--in-person'
              "
            >
              <v-icon size="11" left>
                {{
                  meetingType === "online"
                    ? "mdi-video"
                    : "mdi-hospital-building"
                }}
              </v-icon>
              {{ meetingType === "online" ? "Online" : "Presencial" }}
            </v-chip>
            <v-chip
              v-if="isExternal"
              x-small
              outlined
              dark
              class="ev-chip ev-chip--external"
            >
              <v-icon size="11" left>mdi-google</v-icon>
              Externo
            </v-chip>
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          small
          color="white"
          class="close-btn"
          aria-label="Ver tour desta janela"
          @click="startTour"
        >
          <v-icon>mdi-help-circle-outline</v-icon>
        </v-btn>
        <v-btn icon color="white" class="close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Loading inicial -->
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        height="2"
      />

      <v-card-text class="details-body">
        <!-- salvia-789 (D5): a recusa so se resolve com acao. O medico chega aqui
             pela notificacao; sem uma sugestao, ele veria a consulta e teria que
             deduzir sozinho o que fazer. Sugere, nao executa. -->
        <v-alert
          v-if="foiRecusada"
          dense
          text
          type="error"
          class="recusa-alerta"
        >
          <div class="recusa-texto">
            <strong>A paciente avisou que não vem.</strong>
            O horário continua reservado até você decidir.
          </div>
          <div class="recusa-acoes">
            <v-btn
              small
              color="error"
              depressed
              :loading="cancelando"
              @click="onCancelarConsulta"
            >
              <v-icon left small>mdi-calendar-remove</v-icon>
              Cancelar e liberar o horário
            </v-btn>
            <v-btn
              v-if="canReschedule"
              small
              outlined
              color="primary"
              class="ml-2"
              :loading="rescheduling"
              @click="onReschedule"
            >
              <v-icon left small>mdi-calendar-sync</v-icon>
              Remanejar
            </v-btn>
          </div>
        </v-alert>

        <!-- Aviso pra eventos externos -->
        <v-alert
          v-if="isExternal"
          dense
          text
          color="grey darken-1"
          icon="mdi-lock-outline"
          class="external-alert"
        >
          Evento criado fora do Nutria. Não pode ser editado por aqui.
        </v-alert>

        <!-- ═══ Data e hora ═══ -->
        <section class="ds-section">
          <div class="ds-section-header">
            <v-icon size="15" color="primary" class="mr-2"
              >mdi-calendar-clock</v-icon
            >
            <span class="ds-section-title">Data e horário</span>
          </div>
          <div class="ds-section-body">
            <div class="info-row">
              <v-icon size="14" color="grey darken-1" class="mr-2"
                >mdi-calendar</v-icon
              >
              <span>{{ formattedDateRange }}</span>
            </div>
            <div v-if="durationLabel" class="info-row info-row--muted">
              <v-icon size="14" color="grey darken-1" class="mr-2"
                >mdi-timer-sand</v-icon
              >
              <span>{{ durationLabel }}</span>
            </div>
          </div>
        </section>

        <!-- Linha com Paciente + Profissional lado a lado -->
        <div v-if="patient || doctor" class="ds-grid-2">
          <!-- ═══ Paciente ═══ -->
          <section v-if="patient" class="ds-section" data-tour="det-paciente">
            <div class="ds-section-header">
              <v-icon size="15" color="primary" class="mr-2"
                >mdi-account</v-icon
              >
              <span class="ds-section-title">Paciente</span>
            </div>
            <div class="ds-section-body">
              <div class="person-card">
                <v-avatar size="36" class="person-avatar">
                  <img :src="patientPhoto" :alt="patient.name" />
                </v-avatar>
                <div class="person-info">
                  <div class="person-name">{{ patient.name }}</div>
                  <div class="person-meta">
                    <span v-if="patient.phone" class="meta-item">
                      <v-icon size="12" class="mr-1">mdi-phone-outline</v-icon>
                      {{ patient.phone }}
                    </span>
                    <span v-if="patient.email" class="meta-item">
                      <v-icon size="12" class="mr-1">mdi-email-outline</v-icon>
                      {{ patient.email }}
                    </span>
                  </div>
                </div>
              </div>

              <!--
                salvia-837 — pedido do Vidal (wireframe de 03/09).

                Fica no cartao PACIENTE, e nao no rodape, porque e navegacao
                PARA o paciente — mesma familia do "Abrir prontuario" logo
                abaixo. O rodape e outra conversa: Remanejar, Excluir e Editar
                agem sobre o COMPROMISSO.

                Acima, e nao abaixo, porque no dia da consulta atender e o que
                se faz primeiro; abrir o prontuario e a excecao.

                A ata do T41 pediu vermelho. Aqui nao da: vermelho nesta tela ja
                e "Excluir", no rodape. Dois vermelhos com sentidos opostos na
                mesma caixa custam mais do que o destaque rende.
              -->
              <button
                v-if="canStartAppointment"
                type="button"
                class="dashboard-shortcut dashboard-shortcut--start"
                @click="iniciarAtendimento"
              >
                <v-icon size="14" color="white" class="mr-1"
                  >mdi-play-circle-outline</v-icon
                >
                <span>Iniciar atendimento</span>
                <v-icon size="14" color="white" class="ml-auto"
                  >mdi-arrow-right</v-icon
                >
              </button>

              <button
                v-if="podeVerProntuario"
                type="button"
                class="dashboard-shortcut"
                @click="openPatientDashboard"
              >
                <v-icon size="14" color="white" class="mr-1"
                  >mdi-clipboard-account</v-icon
                >
                <span>Abrir prontuário</span>
                <v-icon size="14" color="white" class="ml-auto"
                  >mdi-arrow-right</v-icon
                >
              </button>
            </div>
          </section>

          <!-- ═══ Médico ═══ -->
          <section v-if="doctor" class="ds-section">
            <div class="ds-section-header">
              <v-icon size="15" color="primary" class="mr-2"
                >mdi-stethoscope</v-icon
              >
              <span class="ds-section-title">Profissional</span>
            </div>
            <div class="ds-section-body">
              <div class="person-card">
                <v-avatar
                  size="36"
                  class="person-avatar person-avatar--doctor"
                  :style="
                    details && details.doctor_color
                      ? { borderColor: details.doctor_color }
                      : {}
                  "
                >
                  <img :src="doctorPhoto" :alt="doctor.name" />
                </v-avatar>
                <div class="person-info">
                  <div class="person-name">{{ doctor.name }}</div>
                  <div class="person-meta">
                    <span v-if="doctor.registration_label" class="meta-item">
                      <v-icon size="12" class="mr-1"
                        >mdi-card-account-details-outline</v-icon
                      >
                      {{ doctor.registration_label }}
                    </span>
                    <span v-if="doctor.email" class="meta-item">
                      <v-icon size="12" class="mr-1">mdi-email-outline</v-icon>
                      {{ doctor.email }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- ═══ Bloco do Meet ═══ -->
        <section v-if="meetingLink" class="meet-block">
          <div class="meet-block-header">
            <v-icon size="15" color="success" class="mr-2">mdi-video</v-icon>
            <strong>Atendimento online</strong>
          </div>
          <div class="meet-block-actions">
            <v-btn
              color="success"
              class="meet-join-btn flex-grow-1 mr-2"
              :href="meetingLink"
              target="_blank"
              rel="noopener noreferrer"
              depressed
              small
            >
              <v-icon left small>mdi-video</v-icon>
              Entrar no Meet
            </v-btn>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="copyMeetLink"
                >
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>Copiar link</span>
            </v-tooltip>
          </div>
        </section>

        <!-- ═══ Clínica ═══ -->
        <section v-if="clinic" class="ds-section">
          <div class="ds-section-header">
            <v-icon size="15" color="primary" class="mr-2"
              >mdi-hospital-building</v-icon
            >
            <span class="ds-section-title">Clínica</span>
          </div>
          <div class="ds-section-body">
            <div class="clinic-card">
              <div class="clinic-name">{{ clinic.name }}</div>
              <div class="clinic-meta">
                <span v-if="clinic.cnpj" class="meta-item">
                  <v-icon size="12" class="mr-1">mdi-domain</v-icon>
                  {{ clinic.cnpj }}
                </span>
                <span v-if="clinicLocation" class="meta-item">
                  <v-icon size="12" class="mr-1">mdi-map-marker-outline</v-icon>
                  {{ clinicLocation }}
                </span>
                <span v-if="clinic.phone" class="meta-item">
                  <v-icon size="12" class="mr-1">mdi-phone-outline</v-icon>
                  {{ clinic.phone }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- ═══ Convidados ═══ -->
        <section v-if="attendees.length" class="ds-section">
          <div class="ds-section-header">
            <v-icon size="15" color="primary" class="mr-2"
              >mdi-account-group</v-icon
            >
            <span class="ds-section-title">
              Convidados ({{ attendees.length }})
            </span>
          </div>
          <div class="ds-section-body ds-section-body--tight">
            <div
              v-for="(att, i) in attendees"
              :key="att.email + i"
              class="attendee-row"
            >
              <div class="attendee-info">
                <div class="attendee-name">
                  {{ att.display_name || att.email }}
                  <v-chip
                    v-if="att.organizer"
                    x-small
                    class="ml-1 attendee-tag"
                    color="primary"
                    outlined
                  >
                    Organizador
                  </v-chip>
                </div>
                <div v-if="att.display_name" class="attendee-email">
                  {{ att.email }}
                </div>
              </div>
              <v-chip
                x-small
                :color="responseChipColor(att.response_status)"
                :text-color="responseChipTextColor(att.response_status)"
                class="attendee-response"
              >
                <v-icon size="11" left>{{
                  responseChipIcon(att.response_status)
                }}</v-icon>
                {{ responseLabel(att.response_status) }}
              </v-chip>
            </div>
          </div>
        </section>

        <!-- ═══ Local ═══ -->
        <section v-if="location" class="ds-section">
          <div class="ds-section-header">
            <v-icon size="15" color="primary" class="mr-2"
              >mdi-map-marker</v-icon
            >
            <span class="ds-section-title">Local</span>
          </div>
          <div class="ds-section-body">
            <div class="info-row">{{ location }}</div>
          </div>
        </section>

        <!-- ═══ Organizador (sempre que o Google informar; no modelo de evento
             único é o médico dono do evento) ═══ -->
        <section v-if="organizer" class="ds-section">
          <div class="ds-section-header">
            <v-icon size="15" color="primary" class="mr-2"
              >mdi-account-tie</v-icon
            >
            <span class="ds-section-title">Organizador</span>
          </div>
          <div class="ds-section-body">
            <div class="info-row">
              <v-icon size="14" color="grey darken-1" class="mr-2"
                >mdi-email-outline</v-icon
              >
              <span>{{ organizer.name || organizer.email }}</span>
            </div>
          </div>
        </section>

        <!-- ═══ Observações / descrição ═══ -->
        <section v-if="description" class="ds-section">
          <div class="ds-section-header">
            <v-icon size="15" color="primary" class="mr-2"
              >mdi-note-text-outline</v-icon
            >
            <span class="ds-section-title">Observações</span>
          </div>
          <div class="ds-section-body">
            <p class="description-text">{{ description }}</p>
          </div>
        </section>
      </v-card-text>

      <!-- Ações -->
      <v-card-actions class="ds-dialog-actions">
        <v-btn
          outlined
          color="grey darken-1"
          class="ds-cancel-btn"
          small
          @click="close"
        >
          <v-icon left small>mdi-close</v-icon>
          Fechar
        </v-btn>
        <v-spacer />
        <template v-if="!isExternal">
          <div class="d-flex align-center" data-tour="det-acoes">
            <v-btn
              v-if="canReschedule"
              color="primary"
              outlined
              small
              class="ds-cancel-btn mr-2"
              :loading="rescheduling"
              @click="onReschedule"
            >
              <v-icon left small>mdi-calendar-sync</v-icon>
              Remanejar
            </v-btn>
            <v-btn
              color="error"
              outlined
              small
              class="ds-cancel-btn mr-2"
              @click="onDelete"
            >
              <v-icon left small>mdi-trash-can-outline</v-icon>
              Excluir
            </v-btn>
            <span v-if="hasUnavailableData" class="text-caption mr-2">
              Dados indisponíveis no momento — edição bloqueada
            </span>
            <v-btn
              v-else
              color="primary"
              class="ds-save-btn"
              small
              dark
              depressed
              @click="onEdit"
            >
              <v-icon left small>mdi-pencil</v-icon>
              Editar
            </v-btn>
          </div>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import { getProfileImage } from "@/utils/getProfileImage";
import { safeUrl } from "@/utils/sanitizeHtml";
import { hasUnreadableFields } from "@/utils/unreadableFields";
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import http from "@/services/http";
import { cancelarConsulta } from "@/services/eventCancelService";

const FALLBACK_AVATAR = require("@/assets/images/profile_default_image.png");

/**
 * Status em que o compromisso ja acabou (bem ou mal) e nao cabe mais agir sobre
 * ele. Era uma lista solta dentro do `canReschedule`; virou constante quando a
 * salvia-837 passou a precisar da MESMA regra para "Iniciar atendimento".
 *
 * Duas listas separadas divergiriam no primeiro status novo — e divergir aqui
 * significa oferecer atendimento num compromisso cancelado.
 */
const STATUS_ENCERRADOS = ["canceled", "cancelled", "completed", "blocked"];

export default {
  name: "CalendarEventDetailsDialog",

  mixins: [tourDialogMixin],

  props: {
    value: { type: Boolean, default: false },
    // Dado bruto do v-calendar (fallback quando o endpoint de detalhes não
    // respondeu ainda ou falhou).
    event: { type: Object, default: null },
    // Payload rico vindo de /api/google-calendar/event-details/{id}
    details: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },

  emits: ["input", "edit", "delete"],

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      patientPhoto: FALLBACK_AVATAR,
      doctorPhoto: FALLBACK_AVATAR,
      _patientBlobUrl: null,
      _doctorBlobUrl: null,
      rescheduling: false,
      cancelando: false,
    };
  },

  watch: {
    "details.patient.profile_image": {
      immediate: true,
      handler(newVal) {
        this.loadAvatar("patient", newVal);
      },
    },
    "details.doctor.profile_image": {
      immediate: true,
      handler(newVal) {
        this.loadAvatar("doctor", newVal);
      },
    },
    value(open) {
      // Ao fechar, libera os blobs pra não vazar memória
      if (!open) this.revokeAvatars();
      else this.tourOnOpen(); // tour guiado na 1ª vez que abre
    },
  },

  beforeDestroy() {
    this.revokeAvatars();
  },

  computed: {
    tourId() {
      return "dialog-detalhes-evento";
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },

    displayName() {
      return this.details?.name || this.event?.name || "Evento sem título";
    },

    isExternal() {
      const src = this.details?.source ?? this.event?.source;
      return src === "external";
    },

    // ID do Event (agenda do Laravel). Eventos externos (só Google) vêm com id
    // null e não podem ser remanejados por aqui.
    eventId() {
      return this.details?.id ?? this.event?.id ?? null;
    },

    /**
     * A paciente recusou (salvia-789). Vem do backend em `confirmation_state`;
     * `patient_declined_at` é o fallback para payloads que ainda não trazem o
     * estado calculado.
     */
    foiRecusada() {
      const estado =
        this.details?.confirmation_state ?? this.event?.confirmation_state;
      if (estado) return estado === "declined";
      return Boolean(
        this.details?.patient_declined_at ?? this.event?.patient_declined_at,
      );
    },

    /**
     * O evento veio com campo que não abriu no cofre (título, descrição…). Editar
     * ou remanejar gravaria o texto de reserva por cima do verdadeiro — então
     * os dois somem até o cofre voltar (15/10/2026).
     */
    hasUnavailableData() {
      return hasUnreadableFields(this.details) || hasUnreadableFields(this.event);
    },

    // Só remaneja consulta do projeto, com paciente e ainda não finalizada/cancelada.
    canReschedule() {
      const status = this.details?.status ?? this.event?.status;
      const hasPatient = !!(
        this.details?.patient?.id ?? this.event?.patient?.id
      );
      return (
        !this.isExternal &&
        !!this.eventId &&
        hasPatient &&
        !this.hasUnavailableData &&
        !STATUS_ENCERRADOS.includes(status)
      );
    },

    /**
     * salvia-837 — atalho para o atendimento, a partir do detalhe do
     * compromisso.
     *
     * MESMA porta do remanejar (decisao de produto): consulta do projeto, com
     * paciente, ainda nao encerrada.
     *
     * Mais o papel. A agenda e `needsAdminAccess` (roleId 1 ou 3), mas o
     * destino `/patient-profile-dashboard/:id` e `needsToBeADoctor` (roleId 1):
     * para a assistente, o router devolveria para a home. Botao que leva para
     * lugar nenhum e pior que botao ausente, entao ela nao o ve.
     */
    /**
     * Prontuário é só do médico: a rota `/patient-profile-dashboard/:id` é
     * `needsToBeADoctor` e a API barra a assistente com 403 (salvia-745).
     * O "Abrir prontuário" aparecia para ela mesmo assim — e o clique a
     * devolvia para a home. Mesma regra do "Iniciar atendimento" abaixo.
     */
    podeVerProntuario() {
      return this.$store.getters["auth/roleId"] === 1;
    },

    canStartAppointment() {
      const status = this.details?.status ?? this.event?.status;
      const hasPatient = !!(
        this.details?.patient?.id ?? this.event?.patient?.id
      );

      return (
        this.podeVerProntuario &&
        !this.isExternal &&
        !!this.eventId &&
        hasPatient &&
        !STATUS_ENCERRADOS.includes(status)
      );
    },

    description() {
      return this.details?.description || this.event?.description || "";
    },

    meetingType() {
      return this.details?.meeting_type ?? this.event?.meeting_type ?? null;
    },

    /**
     * Vai direto para um `:href`, e o valor vem do evento (Google Calendar /
     * banco) — não é digitado por nós. `safeUrl` garante que só esquema
     * navegável passe; com `javascript:` o link simplesmente não é exibido,
     * porque o `v-if` também depende deste computed.
     */
    meetingLink() {
      return safeUrl(
        this.details?.meeting_link || this.event?.meeting_link || null,
      );
    },

    statusLabel() {
      if (this.details?.status_label) return this.details.status_label;
      const map = {
        waiting: "Em espera",
        scheduled: "Agendado",
        confirmed: "Confirmado",
        no_show: "Falta",
        canceled: "Cancelado",
        blocked: "Compromisso",
      };
      const s = this.details?.status ?? this.event?.status;
      return s ? map[s] || null : null;
    },

    statusColor() {
      return (
        this.details?.status_color ||
        this.event?.status_color ||
        this.event?.color ||
        "#2196F3"
      );
    },

    patient() {
      return this.details?.patient || null;
    },

    doctor() {
      return this.details?.doctor || null;
    },

    clinic() {
      return this.details?.clinic || null;
    },

    clinicLocation() {
      const c = this.clinic;
      if (!c) return null;
      const parts = [];
      if (c.city) parts.push(c.city);
      if (c.state) parts.push(c.state);
      return parts.length ? parts.join(" / ") : null;
    },

    attendees() {
      return this.details?.attendees || [];
    },

    location() {
      return this.details?.location || null;
    },

    organizer() {
      const o = this.details?.organizer;
      if (o && (o.email || o.name)) return o;
      const eo = this.event?.organizer;
      if (typeof eo === "string" && eo) return { email: eo, name: null };
      return null;
    },

    formattedDateRange() {
      const start = this.details?.start || this.event?.start;
      const end = this.details?.end || this.event?.end;
      return this.formatRange(start, end);
    },

    durationLabel() {
      const m = this.details?.duration_min;
      if (m == null) {
        const start = this.details?.start || this.event?.start;
        const end = this.details?.end || this.event?.end;
        if (!start || !end) return null;
        const diff = Math.abs(
          (new Date(end).getTime() - new Date(start).getTime()) / 60000,
        );
        return this.formatDuration(diff);
      }
      return this.formatDuration(m);
    },
  },

  methods: {
    /**
     * Cancela pelo endpoint da 788 — que preserva o registro da consulta e
     * devolve o horário como vaga para o encaixe (706/707). Diferente de
     * "Excluir", que tira o evento do Google Calendar.
     */
    async onCancelarConsulta() {
      if (!this.eventId || this.cancelando) return;

      this.cancelando = true;
      try {
        await cancelarConsulta(this.eventId, "Paciente recusou a consulta");
        this.success(
          "Consulta cancelada. O horário voltou a ficar disponível.",
        );
        this.$emit("canceled", this.eventId);
        this.close();
      } catch (e) {
        this.erroDeRequisicao(e, "Não foi possível cancelar a consulta.");
      } finally {
        this.cancelando = false;
      }
    },

    // Tour guiado (tourDialogMixin fornece tourOnOpen/startTour).
    tourReady() {
      return !this.loading; // espera os detalhes carregarem
    },
    tourSteps() {
      return [
        {
          anchor: "det-titulo",
          title: "Detalhes do atendimento",
          description:
            "Esta janela resume o evento selecionado: título, status e se é presencial ou online.",
        },
        {
          anchor: "det-paciente",
          title: "Paciente",
          description:
            "Os dados do paciente atendido aparecem aqui, com atalho para abrir o perfil dele.",
        },
        {
          anchor: "det-acoes",
          title: "Editar ou excluir",
          description:
            "Use estes botões para ajustar o atendimento ou removê-lo da agenda.",
        },
      ];
    },

    async loadAvatar(target, imageName) {
      const blobKey =
        target === "patient" ? "_patientBlobUrl" : "_doctorBlobUrl";
      const photoKey = target === "patient" ? "patientPhoto" : "doctorPhoto";

      // revoga o anterior antes de pedir o novo
      if (this[blobKey]) {
        URL.revokeObjectURL(this[blobKey]);
        this[blobKey] = null;
      }

      if (!imageName) {
        this[photoKey] = FALLBACK_AVATAR;
        return;
      }

      const url = await getProfileImage(imageName);
      if (typeof url === "string" && url.startsWith("blob:")) {
        this[blobKey] = url;
      }
      this[photoKey] = url;
    },

    revokeAvatars() {
      if (this._patientBlobUrl) {
        URL.revokeObjectURL(this._patientBlobUrl);
        this._patientBlobUrl = null;
      }
      if (this._doctorBlobUrl) {
        URL.revokeObjectURL(this._doctorBlobUrl);
        this._doctorBlobUrl = null;
      }
      this.patientPhoto = FALLBACK_AVATAR;
      this.doctorPhoto = FALLBACK_AVATAR;
    },

    close() {
      this.internalValue = false;
    },

    onEdit() {
      this.$emit("edit", this.event);
    },

    onDelete() {
      const id = this.event?.google_id || this.details?.google_id;
      this.$emit("delete", id);
    },

    // Dispara a oferta de remanejamento: o backend calcula horários parecidos e a
    // IA manda ao paciente por WhatsApp pra escolher. Não muda a consulta agora —
    // ela só é movida quando o paciente responde.
    async onReschedule() {
      const id = this.eventId;
      if (!id) {
        this.error("Não foi possível identificar a consulta.");
        return;
      }
      this.rescheduling = true;
      try {
        await http.post(`/api/appointments/${id}/reschedule/offer`);
        // O envio é em DOIS PASSOS, por desenho (ver o docstring de
        // `send_reschedule_offer` na comunicação): aqui sai só o convite
        // estático, que abre a janela de 24h; a lista numerada de horários fica
        // guardada e é disparada sozinha quando o paciente responde.
        //
        // O texto anterior dizia "Oferta de remanejamento enviada", o que fazia
        // o profissional acreditar que os horários já tinham ido. Ele então
        // esperava uma resposta a uma lista que a paciente nunca viu. Não era
        // falha — era a frase prometendo mais do que aconteceu.
        this.success(
          "Convite enviado por WhatsApp. Assim que o paciente responder, os " +
            "horários vão automaticamente.",
        );
        this.close();
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          "Não foi possível enviar a oferta de remanejamento.";
        this.erroDeRequisicao(err, msg);
      } finally {
        this.rescheduling = false;
      }
    },

    /**
     * salvia-837: leva ao prontuario JA com o atendimento aberto — sem etapa
     * intermediaria de escolher o paciente (e o que separa esta issue da
     * salvia-778).
     *
     * O estado inicial que o wireframe exige (cronometro 00:00, sem gravacao,
     * "Nenhuma anotacao ainda", motivo sem selecao) nao precisa ser montado
     * aqui: `openAppointmentDialog(null)` no dashboard ja abre um atendimento
     * NOVO, que nasce assim.
     *
     * NAO mexe no status do compromisso. "Em atendimento" nao existe no mapa do
     * backend, e cria-lo seria migration + kanban + webhook — outra task.
     */
    iniciarAtendimento() {
      const patientId = this.patient?.id;
      if (!patientId) {
        this.error("Não foi possível identificar o paciente.");
        return;
      }
      this.close();
      // `event`: o atendimento nasce ligado a ESTE agendamento (histórico do
      // kanban), mesmo com duas consultas do paciente no dia.
      this.$router.push({
        path: `/patient-profile-dashboard/${patientId}`,
        query: { atendimento: "novo", ...(this.eventId ? { event: String(this.eventId) } : {}) },
      });
    },

    openPatientDashboard() {
      const patientId = this.patient?.id;
      if (!patientId) {
        this.error("Não foi possível identificar o paciente.");
        return;
      }
      this.close();
      this.$router.push(`/patient-profile-dashboard/${patientId}`);
    },

    copyMeetLink() {
      if (!this.meetingLink) return;
      navigator.clipboard
        .writeText(this.meetingLink)
        .then(() => this.success("Link do Meet copiado!"))
        .catch(() => this.error("Não foi possível copiar o link."));
    },

    formatRange(start, end) {
      if (!start || !end) return "—";
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return "Data inválida";
      }

      const locale = "pt-BR";
      const sameDay = startDate.toDateString() === endDate.toDateString();

      const dayFmt = new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
      });
      const timeFmt = new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      if (sameDay) {
        return `${dayFmt.format(startDate)} · ${timeFmt.format(
          startDate,
        )} – ${timeFmt.format(endDate)}`;
      }
      return `${dayFmt.format(startDate)} ${timeFmt.format(
        startDate,
      )} → ${dayFmt.format(endDate)} ${timeFmt.format(endDate)}`;
    },

    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return null;
      const total = Math.round(minutes);
      if (total < 60) return `${total} min`;
      const h = Math.floor(total / 60);
      const m = total % 60;
      return m === 0 ? `${h} h` : `${h} h ${m} min`;
    },

    responseLabel(status) {
      switch (status) {
        case "accepted":
          return "Aceitou";
        case "declined":
          return "Recusou";
        case "tentative":
          return "Talvez";
        case "needsAction":
        default:
          return "Aguardando";
      }
    },

    responseChipColor(status) {
      switch (status) {
        case "accepted":
          return "#E8F5E9";
        case "declined":
          return "#FFEBEE";
        case "tentative":
          return "#FFF3E0";
        case "needsAction":
        default:
          return "#ECEFF1";
      }
    },

    responseChipTextColor(status) {
      switch (status) {
        case "accepted":
          return "#2E7D32";
        case "declined":
          return "#C62828";
        case "tentative":
          return "#E65100";
        case "needsAction":
        default:
          return "#546E7A";
      }
    },

    responseChipIcon(status) {
      switch (status) {
        case "accepted":
          return "mdi-check-circle";
        case "declined":
          return "mdi-close-circle";
        case "tentative":
          return "mdi-help-circle";
        case "needsAction":
        default:
          return "mdi-clock-outline";
      }
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 14px 40px rgba(31, 77, 63, 0.16), 0 3px 10px rgba(0, 0, 0, 0.05) !important;
}

.ds-dialog-header {
  background: linear-gradient(
    135deg,
    var(--ds-primary) 0%,
    var(--ds-primary-deep) 100%
  ) !important;
  color: var(--ds-white) !important;
  padding: 10px 16px !important;
  position: relative;
  align-items: flex-start;
  min-height: unset !important;
}

.ds-dialog-header::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 24px 24px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(
    ellipse at center,
    black 30%,
    transparent 75%
  );
  pointer-events: none;
}

.header-icon {
  position: relative;
  z-index: 1;
  margin-top: 2px;
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.ds-dialog-title {
  font-size: 14px !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
  color: var(--ds-white) !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.header-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.ev-chip {
  height: 18px !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px;
  padding: 0 6px !important;
}

.ev-chip--status {
  color: var(--ds-white) !important;
}

.ev-chip--online {
  background: var(--ds-success-light) !important;
}

.ev-chip--in-person {
  background: rgba(255, 255, 255, 0.18) !important;
}

.ev-chip--external {
  border-color: rgba(255, 255, 255, 0.55) !important;
  color: var(--ds-white) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Corpo ═══ */
.details-body {
  padding: 12px 14px 4px !important;
  background: var(--ds-blue-bg-soft);
}

.external-alert {
  border-radius: 8px !important;
  font-size: 11.5px;
  margin-bottom: 10px !important;
  padding: 6px 10px !important;
}

/* Grid 2 colunas para Paciente + Profissional */
.ds-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

.ds-grid-2 > .ds-section {
  margin-bottom: 0;
}

/* Cada seção */
.ds-section {
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
}

.ds-section-header {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background: var(--ds-blue-50);
  border-bottom: 1px solid var(--ds-blue-100);
}

.ds-section-title {
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.ds-section-body {
  padding: 8px 10px;
}

.ds-section-body--tight {
  padding: 2px 10px;
}

/* Info rows */
.info-row {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--ds-text-strong);
  line-height: 1.4;
  margin-bottom: 2px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row--muted {
  color: var(--ds-text-500);
  font-size: 11.5px;
}

/* ═══ Person card (paciente / médico) ═══ */
.person-card {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dashboard-shortcut {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 8px;
  padding: 7px 12px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    var(--ds-primary) 0%,
    var(--ds-primary-dark) 100%
  );
  color: var(--ds-white);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.25);
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
}

.dashboard-shortcut:hover {
  background: linear-gradient(
    135deg,
    var(--ds-primary-dark) 0%,
    var(--ds-primary-darker) 100%
  );
  box-shadow: 0 4px 10px rgba(31, 77, 63, 0.32);
  transform: translateY(-1px);
}

.dashboard-shortcut:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(31, 77, 63, 0.25);
}

/* Iniciar atendimento: ambar (acento areia, escurecido para o branco ter
   contraste) para nao se confundir com o verde de "Abrir prontuario" logo
   abaixo — nem com o vermelho de "Excluir" no rodape. */
.dashboard-shortcut--start {
  background: linear-gradient(135deg, #c2410c 0%, var(--ds-cat-amber) 100%);
  box-shadow: 0 2px 6px rgba(180, 83, 9, 0.3);
}

.dashboard-shortcut--start:hover {
  background: linear-gradient(135deg, var(--ds-cat-amber) 0%, #92400e 100%);
  box-shadow: 0 4px 10px rgba(180, 83, 9, 0.38);
}

.person-avatar {
  background: var(--ds-blue-50) !important;
  flex-shrink: 0;
  border: 2px solid var(--ds-primary);
}

.person-avatar :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.person-avatar--doctor {
  border-color: var(--ds-success-light);
}

.person-info {
  min-width: 0;
  flex: 1;
}

.person-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-strong);
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.person-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.meta-item {
  font-size: 11.5px;
  color: var(--ds-text-550);
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ═══ Clínica ═══ */
.clinic-card {
  display: flex;
  flex-direction: column;
}

.clinic-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-strong);
  margin-bottom: 2px;
}

.clinic-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}

/* ═══ Attendees ═══ */
.attendee-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f4f9f6;
}

.attendee-row:last-child {
  border-bottom: none;
}

.attendee-info {
  flex: 1;
  min-width: 0;
}

.attendee-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-text-strong);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}

.attendee-email {
  font-size: 11px;
  color: var(--ds-text-soft);
}

.attendee-tag {
  height: 16px !important;
  font-size: 9.5px !important;
  font-weight: 600 !important;
}

.attendee-response {
  height: 18px !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px;
  flex-shrink: 0;
}

/* ═══ Meet block ═══ */
.meet-block {
  background: var(--ds-green-surface);
  border: 1px solid var(--ds-green-200);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.meet-block-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
}

.meet-block-actions {
  display: flex;
  align-items: center;
}

.meet-join-btn {
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  border-radius: 6px !important;
}

.description-text {
  margin: 0;
  font-size: 12.5px;
  color: var(--ds-text-700);
  white-space: pre-wrap;
  line-height: 1.45;
}

/* ═══ Action buttons ═══ */
.ds-dialog-actions {
  padding: 10px 14px !important;
  background: var(--ds-blue-50);
  border-top: 1px solid var(--ds-blue-100);
}

.ds-cancel-btn,
.ds-save-btn {
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  height: 32px !important;
  border-radius: 6px !important;
}

.ds-save-btn {
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.22) !important;
}

@media (max-width: 600px) {
  .details-body {
    padding: 10px 12px 4px !important;
  }
  .ds-grid-2 {
    grid-template-columns: 1fr;
  }
  .ds-dialog-actions {
    padding: 10px 12px !important;
  }
}
/* salvia-789 — sugestão de desfecho para a consulta recusada. */
.recusa-alerta {
  margin-bottom: 14px;
}
.recusa-texto {
  font-size: 13px;
  line-height: 1.45;
  margin-bottom: 8px;
}
.recusa-acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
</style>
